import { useState, useEffect } from 'react';
import { Mail, Server, ShieldCheck, CheckCircle2, AlertCircle, RefreshCw, Edit, Check, Cloud, Key, X, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupportSettingsStore, AwsSesEmailSettings } from '../../../store/supportSettingsStore';
import { useRbacStore } from '../../../store/rbacStore';
import { cn } from '../../../lib/utils';

const SES_REGIONS = [
  { id: 'ap-south-1', name: 'Asia Pacific (Mumbai) [ap-south-1]' },
  { id: 'ap-south-2', name: 'Asia Pacific (Hyderabad) [ap-south-2]' },
  { id: 'us-east-1', name: 'US East (N. Virginia) [us-east-1]' },
  { id: 'us-east-2', name: 'US East (Ohio) [us-east-2]' },
  { id: 'us-west-1', name: 'US West (N. California) [us-west-1]' },
  { id: 'us-west-2', name: 'US West (Oregon) [us-west-2]' },
  { id: 'eu-west-1', name: 'Europe (Ireland) [eu-west-1]' },
  { id: 'eu-west-2', name: 'Europe (London) [eu-west-2]' },
  { id: 'eu-west-3', name: 'Europe (Paris) [eu-west-3]' },
  { id: 'eu-central-1', name: 'Europe (Frankfurt) [eu-central-1]' },
  { id: 'eu-north-1', name: 'Europe (Stockholm) [eu-north-1]' },
  { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore) [ap-southeast-1]' },
  { id: 'ap-southeast-2', name: 'Asia Pacific (Sydney) [ap-southeast-2]' },
  { id: 'ap-northeast-1', name: 'Asia Pacific (Tokyo) [ap-northeast-1]' },
  { id: 'ca-central-1', name: 'Canada (Central) [ca-central-1]' },
  { id: 'sa-east-1', name: 'South America (São Paulo) [sa-east-1]' },
];

const ALLOWED_PORTS = [587, 465, 25, 2465, 2587];

export function MailInfrastructureTab() {
  const {
    sesSettings,
    isLoadingSesSettings,
    isSavingSesSettings,
    isTestingSesConnection,
    isSyncingEmail,
    fetchSesSettings,
    updateSesSettings,
    testSesConnection,
    checkInboundMailNow,
  } = useSupportSettingsStore();

  // Managing the mail provider / email accounts is admin-only on the backend
  // (support settings.email permission). Employees must never see or trigger
  // these calls — gating here means even a direct mount or a stale build can't
  // fire a request that would 403. The route in SupportHub gates this too; this
  // is the defence-in-depth layer at the point of the actual network call.
  const getScope = useRbacStore((s) => s.getScope);
  useRbacStore((s) => s.permissions); // re-evaluate when access loads/changes
  useRbacStore((s) => s.roles);
  const canManageMail = getScope('Support', 'Ticket Settings', 'Edit') === 'Company';

  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [formState, setFormState] = useState<AwsSesEmailSettings>({
    provider: 'aws_ses',
    region: 'ap-south-1',
    send_mode: 'smtp',
    smtp_host: 'email-smtp.ap-south-1.amazonaws.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password_set: false,
    aws_access_key_id: '',
    aws_secret_access_key_set: false,
    sender_email: 'support@trustybytes.in',
    configuration_set: '',
    inbound_enabled: false,
    inbound_method: 'sns_webhook',
    inbound_s3_bucket: '',
    inbound_sns_topic_arn: '',
  });

  // Secret replacement states
  const [replaceSmtpPassword, setReplaceSmtpPassword] = useState(false);
  const [newSmtpPassword, setNewSmtpPassword] = useState('');
  const [replaceAwsSecretKey, setReplaceAwsSecretKey] = useState(false);
  const [newAwsSecretKey, setNewAwsSecretKey] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Test result banner
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Inbound check result banner
  const [inboundCheckResult, setInboundCheckResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (canManageMail) fetchSesSettings();
  }, [fetchSesSettings, canManageMail]);

  useEffect(() => {
    if (sesSettings) {
      setFormState(sesSettings);
    }
  }, [sesSettings]);

  const handleRegionChange = (newRegion: string) => {
    setFormState((prev) => ({
      ...prev,
      region: newRegion,
      smtp_host: `email-smtp.${newRegion}.amazonaws.com`,
    }));
    if (errors.region) {
      setErrors((prev) => ({ ...prev, region: '' }));
    }
  };

  const handleSendModeChange = (mode: 'smtp' | 'api') => {
    if (!isEditing) {
      toast('Click "Edit" above to change delivery mode.', { icon: '🔒' });
      return;
    }
    setFormState((prev) => ({ ...prev, send_mode: mode }));
    setErrors({});
  };

  const handleInboundMethodChange = (method: 'sns_webhook' | 's3') => {
    if (!isEditing) {
      toast('Click "Edit" above to change inbound method.', { icon: '🔒' });
      return;
    }
    setFormState((prev) => ({ ...prev, inbound_method: method }));
    if (errors.inbound_s3_bucket) {
      setErrors((prev) => ({ ...prev, inbound_s3_bucket: '' }));
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formState.sender_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.sender_email.trim())) {
      errs.sender_email = 'Valid sender email address is required (e.g. support@trustybytes.in).';
    }

    if (!formState.region) {
      errs.region = 'AWS region is required.';
    }

    if (formState.send_mode === 'smtp') {
      if (!formState.smtp_host) {
        errs.smtp_host = 'SMTP Host is required.';
      }
      if (!ALLOWED_PORTS.includes(Number(formState.smtp_port))) {
        errs.smtp_port = `Port must be one of: ${ALLOWED_PORTS.join(', ')}`;
      }
      if (!formState.smtp_username || !formState.smtp_username.trim()) {
        errs.smtp_username = 'SMTP Username is required.';
      }
      if (!formState.smtp_password_set && (!newSmtpPassword || !newSmtpPassword.trim())) {
        errs.smtp_password = 'SMTP Password is required.';
      }
    } else {
      // API Mode
      if (!formState.aws_access_key_id || !formState.aws_access_key_id.trim()) {
        errs.aws_access_key_id = 'AWS Access Key ID is required in API mode.';
      }
      if (!formState.aws_secret_access_key_set && (!newAwsSecretKey || !newAwsSecretKey.trim())) {
        errs.aws_secret_access_key = 'AWS Secret Access Key is required in API mode.';
      }
    }

    if (formState.inbound_enabled && formState.inbound_method === 's3') {
      if (!formState.inbound_s3_bucket || !formState.inbound_s3_bucket.trim()) {
        errs.inbound_s3_bucket = 'S3 Bucket name is required when S3 inbound method is selected.';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the validation errors before saving.');
      return;
    }

    const payload: Partial<AwsSesEmailSettings> & { smtp_password?: string; aws_secret_access_key?: string } = {
      ...formState,
      smtp_port: Number(formState.smtp_port),
    };

    if (replaceSmtpPassword && newSmtpPassword.trim()) {
      payload.smtp_password = newSmtpPassword.trim();
    }
    if (replaceAwsSecretKey && newAwsSecretKey.trim()) {
      payload.aws_secret_access_key = newAwsSecretKey.trim();
    }

    const res = await updateSesSettings(payload);
    if (res.success) {
      setIsEditing(false);
      setReplaceSmtpPassword(false);
      setNewSmtpPassword('');
      setReplaceAwsSecretKey(false);
      setNewAwsSecretKey('');
      setErrors({});
    }
  };

  const handleCancel = () => {
    if (sesSettings) {
      setFormState(sesSettings);
    }
    setIsEditing(false);
    setReplaceSmtpPassword(false);
    setNewSmtpPassword('');
    setReplaceAwsSecretKey(false);
    setNewAwsSecretKey('');
    setErrors({});
  };

  const handleTestMailServer = async () => {
    setTestResult(null);
    const testPayload: any = {
      send_mode: formState.send_mode,
      region: formState.region,
    };

    if (formState.send_mode === 'smtp') {
      testPayload.smtp_host = formState.smtp_host;
      testPayload.smtp_port = Number(formState.smtp_port);
      testPayload.smtp_username = formState.smtp_username;
      if (replaceSmtpPassword && newSmtpPassword.trim()) {
        testPayload.smtp_password = newSmtpPassword.trim();
      }
    } else {
      testPayload.aws_access_key_id = formState.aws_access_key_id;
      if (replaceAwsSecretKey && newAwsSecretKey.trim()) {
        testPayload.aws_secret_access_key = newAwsSecretKey.trim();
      }
    }

    const res = await testSesConnection(testPayload);
    setTestResult(res);
  };

  const handleCheckInboundNow = async () => {
    setInboundCheckResult(null);
    const res = await checkInboundMailNow();
    setInboundCheckResult(res);
  };

  if (!canManageMail) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-10 text-center text-foreground">
        <div className="rounded-full bg-muted p-3 text-muted-foreground">
          <Lock className="size-6" />
        </div>
        <h2 className="font-display text-section-title font-semibold">No access</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Mail provider and email account settings can only be managed by a support administrator.
          Please contact your administrator if you need changes here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-foreground font-sans">
      {/* 1. TOP HEADER & GLOBAL ACTIONS */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Mail className="size-5" />
              </div>
              <div>
                <h2 className="font-display text-section-title font-semibold text-foreground">
                  Email Infrastructure
                </h2>
                <p className="text-muted-foreground text-xs">
                  Production email dispatch &amp; receipt configured via Amazon Simple Email Service (AWS SES).
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end">
            {/* Check Mail Now (Retained for S3 Inbound) */}
            <button
              type="button"
              disabled={isSyncingEmail}
              onClick={handleCheckInboundNow}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={cn('size-3.5', isSyncingEmail && 'animate-spin text-primary')} />
              {isSyncingEmail ? 'Checking Mail...' : 'Check Mail Now'}
            </button>

            {/* Test Mail Server Button */}
            <button
              type="button"
              disabled={isTestingSesConnection}
              onClick={handleTestMailServer}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={cn('size-3.5', isTestingSesConnection && 'animate-spin text-primary')} />
              {isTestingSesConnection ? 'Testing Connection...' : 'Test Mail Server'}
            </button>

            {/* Read-Only vs Edit Toggle */}
            {!isEditing ? (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground border border-border text-xs font-semibold">
                  <Lock className="size-3" /> Read-only
                </span>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  <Edit className="size-3.5" /> Edit
                </button>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-warning/10 text-warning-strong border border-warning/20 text-xs font-semibold">
                  <Edit className="size-3" /> Editing
                </span>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  <X className="size-3.5" /> Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingSesSettings}
                  onClick={handleSave}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <Check className="size-3.5" /> {isSavingSesSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Live Test Status Banner */}
        {testResult && (
          <div
            className={cn(
              'mt-4 p-3.5 rounded-lg border text-xs flex items-center justify-between gap-3',
              testResult.success
                ? 'bg-success/10 border-success/30 text-success-strong'
                : 'bg-destructive/10 border-destructive/30 text-destructive'
            )}
          >
            <div className="flex items-center gap-2.5">
              {testResult.success ? (
                <CheckCircle2 className="size-4 shrink-0 text-success" />
              ) : (
                <AlertCircle className="size-4 shrink-0 text-destructive" />
              )}
              <span>{testResult.message}</span>
            </div>
            <button
              type="button"
              onClick={() => setTestResult(null)}
              className="text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        {/* Inbound Check Result Banner */}
        {inboundCheckResult && (
          <div
            className={cn(
              'mt-3 p-3.5 rounded-lg border text-xs flex items-center justify-between gap-3',
              inboundCheckResult.success
                ? 'bg-success/10 border-success/30 text-success-strong'
                : 'bg-destructive/10 border-destructive/30 text-destructive'
            )}
          >
            <div className="flex items-center gap-2.5">
              {inboundCheckResult.success ? (
                <CheckCircle2 className="size-4 shrink-0 text-success" />
              ) : (
                <AlertCircle className="size-4 shrink-0 text-destructive" />
              )}
              <span>{inboundCheckResult.message}</span>
            </div>
            <button
              type="button"
              onClick={() => setInboundCheckResult(null)}
              className="text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        {/* 2. STATIC AMAZON SES HEADER CARD (Replaces provider selector) */}
        <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-primary text-primary-foreground shadow-xs">
              <Cloud className="size-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-card-title font-semibold text-foreground">
                  Amazon Simple Email Service (AWS SES)
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  Active Provider
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border">
                  Default Architecture
                </span>
              </div>
              <p className="text-muted-foreground text-xs mt-0.5">
                Enterprise cloud delivery via AWS SES dedicated IP pools, bounce/complaint metric telemetry, and verified sender authentication.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            <ShieldCheck className="size-4 text-success" />
            <span>AES-256-GCM Encrypted at Rest</span>
          </div>
        </div>

        {/* 3. CONFIGURATION DETAILS */}
        <div className="mt-6 space-y-6">
          {/* Mode Selector & Region Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Delivery Mode Toggle */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                Delivery Mode *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() => handleSendModeChange('smtp')}
                  className={cn(
                    'p-3 rounded-lg border text-left transition-all cursor-pointer flex flex-col justify-between',
                    formState.send_mode === 'smtp'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-foreground hover:bg-muted/50',
                    !isEditing && 'cursor-not-allowed opacity-90'
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-semibold text-xs">SMTP Dispatch</span>
                    {formState.send_mode === 'smtp' && <CheckCircle2 className="size-4 text-primary" />}
                  </div>
                  <span className="text-xs text-muted-foreground mt-1">
                    Standard SMTP submission via ports 587 (STARTTLS) or 465 (SSL)
                  </span>
                </button>

                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() => handleSendModeChange('api')}
                  className={cn(
                    'p-3 rounded-lg border text-left transition-all cursor-pointer flex flex-col justify-between',
                    formState.send_mode === 'api'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-foreground hover:bg-muted/50',
                    !isEditing && 'cursor-not-allowed opacity-90'
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-semibold text-xs">Direct SES v2 API</span>
                    {formState.send_mode === 'api' && <CheckCircle2 className="size-4 text-primary" />}
                  </div>
                  <span className="text-xs text-muted-foreground mt-1">
                    HTTPS AWS SDK v3 client using IAM access credentials
                  </span>
                </button>
              </div>
            </div>

            {/* Region Selector */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                AWS SES Region *
              </label>
              <select
                disabled={!isEditing}
                value={formState.region}
                onChange={(e) => handleRegionChange(e.target.value)}
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                {SES_REGIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              {errors.region && <p className="text-destructive text-xs">{errors.region}</p>}
              <p className="text-muted-foreground text-xs">
                Selecting a region automatically updates the default SES SMTP endpoint.
              </p>
            </div>
          </div>

          {/* Mode-Specific Credential Fields */}
          {formState.send_mode === 'smtp' ? (
            /* SMTP CREDENTIALS */
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="flex items-center gap-2">
                  <Server className="size-4 text-primary" />
                  <span className="font-display text-card-title font-semibold text-foreground">
                    Amazon SES SMTP Credentials
                  </span>
                </div>
                <span className="text-muted-foreground text-xs">
                  Generate in AWS SES &rarr; SMTP Settings
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* SMTP Host */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">SMTP Host *</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    value={formState.smtp_host}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, smtp_host: e.target.value }));
                      if (errors.smtp_host) setErrors((prev) => ({ ...prev, smtp_host: '' }));
                    }}
                    placeholder="email-smtp.ap-south-1.amazonaws.com"
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  />
                  {errors.smtp_host && <p className="text-destructive text-xs">{errors.smtp_host}</p>}
                </div>

                {/* SMTP Port */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">SMTP Port *</label>
                  <select
                    disabled={!isEditing}
                    value={formState.smtp_port}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, smtp_port: Number(e.target.value) }));
                      if (errors.smtp_port) setErrors((prev) => ({ ...prev, smtp_port: '' }));
                    }}
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  >
                    {ALLOWED_PORTS.map((p) => (
                      <option key={p} value={p}>
                        {p} {p === 465 ? '(SSL / TLS)' : p === 587 ? '(STARTTLS — Recommended)' : ''}
                      </option>
                    ))}
                  </select>
                  {errors.smtp_port && <p className="text-destructive text-xs">{errors.smtp_port}</p>}
                </div>

                {/* SMTP Username */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">SMTP Username *</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    value={formState.smtp_username || ''}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, smtp_username: e.target.value }));
                      if (errors.smtp_username) setErrors((prev) => ({ ...prev, smtp_username: '' }));
                    }}
                    placeholder="e.g. AKIA..."
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  />
                  {errors.smtp_username && <p className="text-destructive text-xs">{errors.smtp_username}</p>}
                  <p className="text-muted-foreground text-xs">
                    SES SMTP username is distinct from IAM credentials.
                  </p>
                </div>

                {/* SMTP Password */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">SMTP Password *</label>
                  {formState.smtp_password_set && !replaceSmtpPassword ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        disabled
                        value="••••••••••••••••"
                        className="w-full h-9 rounded-lg border border-border bg-muted px-3 text-xs text-muted-foreground font-mono cursor-not-allowed"
                      />
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => setReplaceSmtpPassword(true)}
                          className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                        >
                          Change
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          disabled={!isEditing}
                          value={newSmtpPassword}
                          onChange={(e) => {
                            setNewSmtpPassword(e.target.value);
                            if (errors.smtp_password) setErrors((prev) => ({ ...prev, smtp_password: '' }));
                          }}
                          placeholder="Enter new SES SMTP password"
                          className={cn(
                            'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                            !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                          )}
                        />
                        {formState.smtp_password_set && isEditing && (
                          <button
                            type="button"
                            onClick={() => {
                              setReplaceSmtpPassword(false);
                              setNewSmtpPassword('');
                            }}
                            className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                          >
                            Keep Existing
                          </button>
                        )}
                      </div>
                      {errors.smtp_password && <p className="text-destructive text-xs">{errors.smtp_password}</p>}
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Encrypted with AES-256-GCM. Secret values are never returned by the API.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* AWS API IAM CREDENTIALS */
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="flex items-center gap-2">
                  <Key className="size-4 text-primary" />
                  <span className="font-display text-card-title font-semibold text-foreground">
                    AWS IAM API Credentials (SES v2)
                  </span>
                </div>
                <span className="text-muted-foreground text-xs">
                  IAM User with permissions: ses:SendEmail, ses:GetAccount
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Access Key ID */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">AWS Access Key ID *</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    value={formState.aws_access_key_id || ''}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, aws_access_key_id: e.target.value }));
                      if (errors.aws_access_key_id) setErrors((prev) => ({ ...prev, aws_access_key_id: '' }));
                    }}
                    placeholder="AKIA..."
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  />
                  {errors.aws_access_key_id && <p className="text-destructive text-xs">{errors.aws_access_key_id}</p>}
                </div>

                {/* Secret Access Key */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">AWS Secret Access Key *</label>
                  {formState.aws_secret_access_key_set && !replaceAwsSecretKey ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        disabled
                        value="••••••••••••••••"
                        className="w-full h-9 rounded-lg border border-border bg-muted px-3 text-xs text-muted-foreground font-mono cursor-not-allowed"
                      />
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => setReplaceAwsSecretKey(true)}
                          className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                        >
                          Change
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          disabled={!isEditing}
                          value={newAwsSecretKey}
                          onChange={(e) => {
                            setNewAwsSecretKey(e.target.value);
                            if (errors.aws_secret_access_key) setErrors((prev) => ({ ...prev, aws_secret_access_key: '' }));
                          }}
                          placeholder="Enter new AWS Secret Access Key"
                          className={cn(
                            'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                            !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                          )}
                        />
                        {formState.aws_secret_access_key_set && isEditing && (
                          <button
                            type="button"
                            onClick={() => {
                              setReplaceAwsSecretKey(false);
                              setNewAwsSecretKey('');
                            }}
                            className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                          >
                            Keep Existing
                          </button>
                        )}
                      </div>
                      {errors.aws_secret_access_key && (
                        <p className="text-destructive text-xs">{errors.aws_secret_access_key}</p>
                      )}
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Encrypted with AES-256-GCM. Secret values are write-only.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SENDER IDENTITY & CONFIGURATION SET */}
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="font-display text-card-title font-semibold text-foreground">
                Sender Identity &amp; Deliverability
              </span>
              <span className="text-muted-foreground text-xs">SES Outbound Identity</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Verified Sender Identity */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">Verified Sender Identity *</label>
                <input
                  type="email"
                  disabled={!isEditing}
                  value={formState.sender_email}
                  onChange={(e) => {
                    setFormState((prev) => ({ ...prev, sender_email: e.target.value }));
                    if (errors.sender_email) setErrors((prev) => ({ ...prev, sender_email: '' }));
                  }}
                  placeholder="support@trustybytes.in"
                  className={cn(
                    'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                    !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                  )}
                />
                {errors.sender_email && <p className="text-destructive text-xs">{errors.sender_email}</p>}
                <p className="text-muted-foreground text-xs">
                  Must be a verified email address or domain in your AWS SES console.
                </p>
              </div>

              {/* SES Configuration Set */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">SES Configuration Set (Optional)</label>
                <input
                  type="text"
                  disabled={!isEditing}
                  value={formState.configuration_set || ''}
                  onChange={(e) => setFormState((prev) => ({ ...prev, configuration_set: e.target.value }))}
                  placeholder="e.g. teamone-default-metrics"
                  className={cn(
                    'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                    !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                  )}
                />
                <p className="text-muted-foreground text-xs">
                  Enables dedicated CloudWatch delivery metrics, open/click events, and bounce logging.
                </p>
              </div>
            </div>
          </div>

          {/* INBOUND EMAIL RECEIPT & TICKET INGESTION */}
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="font-display text-card-title font-semibold text-foreground">
                Inbound Email Ingestion
              </span>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                  <input
                    type="checkbox"
                    disabled={!isEditing}
                    checked={formState.inbound_enabled}
                    onChange={(e) => setFormState((prev) => ({ ...prev, inbound_enabled: e.target.checked }))}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Enable Inbound Ingestion</span>
                </label>
              </div>
            </div>

            {formState.inbound_enabled ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                    Inbound Pipeline Method *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Method 1: SNS Webhook */}
                    <button
                      type="button"
                      disabled={!isEditing}
                      onClick={() => handleInboundMethodChange('sns_webhook')}
                      className={cn(
                        'p-3 rounded-lg border text-left transition-all cursor-pointer flex flex-col justify-between',
                        formState.inbound_method === 'sns_webhook'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-foreground hover:bg-muted/50',
                        !isEditing && 'cursor-not-allowed opacity-90'
                      )}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-semibold text-xs">AWS SES SNS Webhook (Push)</span>
                        {formState.inbound_method === 'sns_webhook' && (
                          <CheckCircle2 className="size-4 text-primary" />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">
                        Instant real-time ticket ingestion via SES Receipt Rule &rarr; SNS Topic &rarr; HTTPS Webhook.
                      </span>
                    </button>

                    {/* Method 2: S3 Bucket */}
                    <button
                      type="button"
                      disabled={!isEditing}
                      onClick={() => handleInboundMethodChange('s3')}
                      className={cn(
                        'p-3 rounded-lg border text-left transition-all cursor-pointer flex flex-col justify-between',
                        formState.inbound_method === 's3'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-foreground hover:bg-muted/50',
                        !isEditing && 'cursor-not-allowed opacity-90'
                      )}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-semibold text-xs">AWS S3 Bucket (Object Storage)</span>
                        {formState.inbound_method === 's3' && <CheckCircle2 className="size-4 text-primary" />}
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">
                        SES stores raw .eml in S3 bucket. TeamOne background worker polls and parses messages.
                      </span>
                    </button>
                  </div>
                </div>

                {/* Conditional Fields based on Inbound Method */}
                {formState.inbound_method === 's3' ? (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">Inbound S3 Bucket Name *</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={formState.inbound_s3_bucket || ''}
                      onChange={(e) => {
                        setFormState((prev) => ({ ...prev, inbound_s3_bucket: e.target.value }));
                        if (errors.inbound_s3_bucket) setErrors((prev) => ({ ...prev, inbound_s3_bucket: '' }));
                      }}
                      placeholder="e.g. trustybytes-inbound-mail"
                      className={cn(
                        'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                        !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}
                    />
                    {errors.inbound_s3_bucket && (
                      <p className="text-destructive text-xs">{errors.inbound_s3_bucket}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      Requires IAM permissions (s3:GetObject, s3:ListBucket) if accessing via API mode.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">Inbound SNS Topic ARN (Optional)</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={formState.inbound_sns_topic_arn || ''}
                      onChange={(e) => setFormState((prev) => ({ ...prev, inbound_sns_topic_arn: e.target.value }))}
                      placeholder="arn:aws:sns:ap-south-1:123456789012:teamone-ses-inbound"
                      className={cn(
                        'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                        !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}
                    />
                    <p className="text-muted-foreground text-xs">
                      Target SNS topic configured in AWS SES Receipt Rule for notification fanout.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">
                Inbound email ingestion is currently disabled. Check the box above to accept customer replies and create support tickets automatically.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
